import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { AlertTriangle, Truck, Package, Download, Filter, Search, ChevronLeft, ChevronRight, MoreVertical, Eye, Edit, AlertCircle, Package2, Database, TrendingUp, Loader2 } from 'lucide-react'
import { cn, formatNumber, formatCurrency } from '../../lib/utils'
import { useQuery } from '@tanstack/react-query'
import { inventoryApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Table } from '../../components/ui/Table'
import { Skeleton } from '../../components/ui/Skeleton'
import { Pagination } from '../../components/ui/Pagination'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  out_of_stock: { label: 'تمام شده', variant: 'danger' as const, color: 'text-danger-600 dark:text-danger-400 bg-danger-100 dark:bg-danger-900/30' },
  critical: { label: 'حراجی', variant: 'danger' as const, color: 'text-danger-600 dark:text-danger-400 bg-danger-100 dark:bg-danger-900/30' },
  low: { label: 'کم', variant: 'warning' as const, color: 'text-warning-600 dark:text-warning-400 bg-warning-100 dark:bg-warning-900/30' },
  ok: { label: 'عادی', variant: 'success' as const, color: 'text-success-600 dark:text-success-400 bg-success-100 dark:bg-success-900/30' },
}

export default function LowStockPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const [filters, setFilters] = useState<LowStockFilters>({
    page: 1,
    page_size: 20,
    sort_by: 'current_stock',
    sort_order: 'asc',
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.lowStock(filters),
    queryFn: () => inventoryApi.lowStock(filters),
    placeholderData: (prev) => prev,
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getStockStatus = (item: any) => {
    if (item.current_stock === 0) return 'out_of_stock'
    if (item.current_stock <= (item.min_stock || 0) * 0.5) return 'critical'
    if (item.current_stock <= (item.min_stock || 0)) return 'low'
    return 'ok'
  }

  const getReorderAction = (item: any) => {
    if (item.current_stock === 0) return { label: 'سفارش فوری', variant: 'danger' as const, icon: AlertCircle }
    if (item.current_stock <= (item.min_stock || 0) * 0.5) return { label: 'سفارش اکسپرس', variant: 'warning' as const, icon: TrendingUp }
    return { label: 'سفارش مجدد', variant: 'primary' as const, icon: Truck }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">موجودی کم و سفارش مجدد</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} کالا نیاز به توجه دارد
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => inventoryApi.exportLowStock(filters).then(blob => {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `low-stock-${new Date().toISOString().split('T')[0]}.xlsx`
            a.click()
            URL.revokeObjectURL(url)
          })}>
            <Download className="w-4 h-4" />
            خروجی اکسل
          </Button>
        </div>
      </motion.div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div className="card p-5 border-danger-200 dark:border-danger-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">تمام شده</p>
              <p className="text-2xl font-bold text-danger-600 dark:text-danger-400 mt-1">
                {data?.summary?.out_of_stock || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-danger-100 dark:bg-danger-900/30 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-danger-600 dark:text-danger-400" />
            </div>
          </div>
        </div>
        <div className="card p-5 border-warning-200 dark:border-warning-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">حراجی</p>
              <p className="text-2xl font-bold text-warning-600 dark:text-warning-400 mt-1">
                {data?.summary?.critical || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">کم</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {data?.summary?.low || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <Package2 className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">مجموع ارزش موجودی کم</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {formatCurrency(data?.summary?.total_value || 0)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Database className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو در نام، SKU، بارکد..." className="flex-1 max-w-md" />
          <Select
            value={filters.warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارها' }]}
            placeholder="انبار"
            className="w-48"
          />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'out_of_stock', label: 'تمام شده' },
              { value: 'critical', label: 'حراجی' },
              { value: 'low', label: 'کم' },
            ]}
            placeholder="وضعیت موجودی"
            className="w-40"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <Table
              columns={[
                { key: 'image', header: '' },
                { key: 'name', header: 'نام کالا' },
                { key: 'sku', header: 'SKU' },
                { key: 'warehouse', header: 'انبار' },
                { key: 'stock', header: 'موجودی' },
                { key: 'min', header: 'حداقل' },
                { key: 'status', header: 'وضعیت' },
                { key: 'action', header: 'عملیات' },
              ]}
              data={[]}
              keyExtractor={() => ''}
              isLoading={true}
            />
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <Package2 className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ کالای کم‌موجودی یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400">تمام کالاهای شما موجودی مناسب دارند</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>تصویر</th>
                    <th>نام کالا</th>
                    <th className="hidden sm:table-cell">SKU</th>
                    <th className="hidden md:table-cell">انبار</th>
                    <th className="hidden lg:table-cell">دسته‌بندی</th>
                    <th>موجودی</th>
                    <th>حداقل</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((item, index) => {
                    const status = getStockStatus(item)
                    const action = getReorderAction(item)
                    const statusInfo = statusConfig[status]

                    return (
                      <motion.tr
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={status === 'out_of_stock' ? 'bg-danger-50 dark:bg-danger-900/20' : ''}
                      >
                        <td className="p-2">
                          <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
                            {item.images?.[0] ? (
                              <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package2 className="w-5 h-5 text-neutral-400" />
                            )}
                          </div>
                        </td>
                        <td className="font-medium text-neutral-900 dark:text-white truncate max-w-xs">{item.name}</td>
                        <td className="hidden sm:table-cell font-mono text-sm text-neutral-700 dark:text-neutral-300">{item.sku}</td>
                        <td className="hidden md:table-cell text-neutral-600 dark:text-neutral-400">{item.warehouse?.name || '—'}</td>
                        <td className="hidden lg:table-cell text-neutral-600 dark:text-neutral-400">{item.category?.name || '—'}</td>
                        <td className="font-mono text-sm font-medium {status === 'out_of_stock' ? 'text-danger-600 dark:text-danger-400' : status === 'critical' ? 'text-warning-600 dark:text-warning-400' : 'text-neutral-900 dark:text-white'}">
                          {formatNumber(item.current_stock)}
                        </td>
                        <td className="font-mono text-sm text-neutral-600 dark:text-neutral-400">{formatNumber(item.min_stock || 0)}</td>
                        <td>
                          <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', statusInfo.color)}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={[
                              { label: 'مشاهده کالا', icon: Eye, onClick: () => window.open(`/inventory/products/${item.id}`, '_blank') },
                              { label: 'مشاهده موجودی', icon: Database, onClick: () => window.open(`/inventory/stock?product=${item.id}`, '_blank') },
                              { type: 'divider' },
                              { label: action.label, icon: action.icon, onClick: () => openModal('reorder'), className: action.variant === 'danger' ? 'text-danger-600' : '' },
                            ]}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Reorder Modal */}
      <Modal
        isOpen={modals['reorder']}
        onClose={() => closeModal('reorder')}
        title="ایجاد سفارش خرید"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-neutral-600 dark:text-neutral-400">
            برای کالاهای انتخاب شده سفارش خرید ایجاد می‌شود.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="تامین‌کننده"
              options={[{ value: '', label: 'انتخاب کنید' }]}
              placeholder="تامین‌کننده"
            />
            <Select
              label="انبار مقصد"
              options={[{ value: '', label: 'انتخاب کنید' }]}
              placeholder="انبار"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => closeModal('reorder')}>انصراف</Button>
            <Button variant="primary"><Truck className="w-4 h-4 ml-1" /> ایجاد سفارش خرید</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

type LowStockFilters = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  warehouse_id?: string
  status?: string
  category_id?: string
}